import type { AvatarPreferences } from '@/types/avatar'
import {
  avatarAccessoryComponents,
  avatarClothingComponents,
  avatarClothingGraphicComponents,
  avatarComponentCatalog,
  avatarEyebrowComponents,
  avatarEyesComponents,
  avatarFacialHairComponents,
  avatarMouthComponents,
  avatarTopComponents,
  type AvatarAccessoryComponentName,
  type AvatarClothingComponentName,
  type AvatarClothingGraphicComponentName,
  type AvatarEyebrowComponentName,
  type AvatarEyesComponentName,
  type AvatarFacialHairComponentName,
  type AvatarMouthComponentName,
  type AvatarTopComponentName,
} from '@/lib/avatar/components'

export const AVATAR_VIEWBOX = '0 0 264 280'

const DEFAULT_BACKGROUND_HEX = '#65C9FF'
const DEFAULT_BODY_BASE_HEX = '#D0C6AC'
const DEFAULT_BODY_SKIN_HEX = '#EDB98A'
const DEFAULT_HAT_PRIMARY = '#1F333C'
const DEFAULT_HAT_ACCENT = '#15232A'
const DEFAULT_FACIAL_HAIR_ACCENT = '#331B0C'
const DEFAULT_FACIAL_HAIR_PRIMARY = '#4A312C'
const DEFAULT_HAIR_PRIMARY = '#724133'
const DEFAULT_HAIR_SHADOW = '#2C1B18'
const DEFAULT_HAIR_MASK = '#361A0A'
const DEFAULT_ACCESSORY_PRIMARY = '#28354B'
const CANVAS_TRANSLATE_X = 32
const CANVAS_TRANSLATE_Y = 36
const CLOTHING_TRANSLATE_X = 0
const CLOTHING_TRANSLATE_Y = 170
const FACE_TRANSLATE_X = 76
const FACE_TRANSLATE_Y = 82
const COLOR_RECT_ID = '🖍Color'

const skinPalette: Record<string, string> = {
  Tanned: '#FD9841',
  Yellow: '#F8D25C',
  Pale: '#FFD9C4',
  Light: '#EDB98A',
  Brown: '#915B4A',
  DarkBrown: '#4A312C',
  Black: '#090806',
}

const topPalette: Record<string, string> = {
  Black: '#262E33',
  Blue01: '#65C9FF',
  Blue02: '#5199E4',
  Blue03: '#25557C',
  Gray01: '#E6E6E6',
  Gray02: '#929598',
  Heather: '#3C4F5C',
  PastelBlue: '#B1E2FF',
  PastelGreen: '#A7FFC4',
  PastelOrange: '#FFDEB5',
  PastelRed: '#FFAFB9',
  PastelYellow: '#FFFFB1',
  Pink: '#FF488E',
  Red: '#FF5C5C',
  White: '#FFFFFF',
}

const clothingPalette: Record<string, string> = {
  Black: '#262E33',
  Blue01: '#65C9FF',
  Blue02: '#5199E4',
  Blue03: '#25557C',
  Gray01: '#E6E6E6',
  Gray02: '#929598',
  Heather: '#3C4F5C',
  PastelBlue: '#B1E2FF',
  PastelGreen: '#A7FFC4',
  PastelOrange: '#FFDEB5',
  PastelRed: '#FFAFB9',
  PastelYellow: '#FFFFB1',
  Pink: '#FF488E',
  Red: '#FF5C5C',
  White: '#FFFFFF',
}

const hairPalette: Record<string, string> = {
  Auburn: '#A55728',
  Black: '#2C1B18',
  Blonde: '#B58143',
  BlondeGolden: '#D6B370',
  Brown: '#724133',
  BrownDark: '#4A312C',
  PastelPink: '#F59797',
  Platinum: '#ECDCBF',
  Red: '#C93305',
  SilverGray: '#E8E1E1',
}

const HEADWEAR_TOP_TYPES = new Set<AvatarTopComponentName>([
  'Hat',
  'Hijab',
  'Turban',
  'WinterHat1',
  'WinterHat2',
  'WinterHat3',
  'WinterHat4',
])

const ACCESSORY_TOP_TYPES = new Set<AvatarTopComponentName>(['Eyepatch'])

function clamp(value: number, min = 0, max = 255) {
  return Math.min(max, Math.max(min, value))
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const normalized = hex.replace('#', '')
  const bigint = parseInt(normalized, 16)
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  }
}

function rgbToHex({ r, g, b }: { r: number; g: number; b: number }) {
  return `#${[r, g, b]
    .map((channel) => clamp(channel).toString(16).padStart(2, '0'))
    .join('')}`
}

function mixWithWhite(hex: string, amount: number) {
  const rgb = hexToRgb(hex)
  return rgbToHex({
    r: Math.round(rgb.r + (255 - rgb.r) * amount),
    g: Math.round(rgb.g + (255 - rgb.g) * amount),
    b: Math.round(rgb.b + (255 - rgb.b) * amount),
  })
}

function mixWithBlack(hex: string, amount: number) {
  const rgb = hexToRgb(hex)
  return rgbToHex({
    r: Math.round(rgb.r * (1 - amount)),
    g: Math.round(rgb.g * (1 - amount)),
    b: Math.round(rgb.b * (1 - amount)),
  })
}

function extractInnerSvg(svg: string) {
  const match = svg.match(/<svg[^>]*>([\s\S]*?)<\/svg>/i)
  return match ? match[1] : svg
}

function applyCircleColor(svg: string, color: string) {
  if (!color) return svg
  const normalized = color.trim()
  return svg.replace(new RegExp(DEFAULT_BACKGROUND_HEX, 'gi'), normalized)
}

function applySkinTones(svg: string, baseHex: string) {
  const lighter = mixWithWhite(baseHex, 0.25)
  return svg
    .replace(new RegExp(DEFAULT_BODY_BASE_HEX, 'gi'), lighter)
    .replace(new RegExp(DEFAULT_BODY_SKIN_HEX, 'gi'), baseHex)
}

function applyHatColors(svg: string, primary: string) {
  const accent = mixWithBlack(primary, 0.25)
  return svg
    .replace(new RegExp(DEFAULT_HAT_PRIMARY, 'gi'), primary)
    .replace(new RegExp(DEFAULT_HAT_ACCENT, 'gi'), accent)
}

function applyAccessoryColors(
  svg: string,
  colorHex?: string,
  componentType?: string,
) {
  if (!svg.trim()) return svg

  const primary = colorHex ?? topPalette.Black
  let updated = svg.replace(new RegExp(DEFAULT_ACCESSORY_PRIMARY, 'gi'), primary)

  if (componentType && componentType.startsWith('Prescription')) {
    const glassesGroupRegex = /(id="Glasses"[^>]*)(>)/i
    if (!/id="Glasses"[^>]*fill-opacity=/i.test(updated)) {
      updated = updated.replace(glassesGroupRegex, (_match, prefix, suffix) => {
        return `${prefix} fill-opacity="0.45"${suffix}`
      })
    }
  }

  return updated
}

function applyFacialHairColors(svg: string, colorName: string) {
  if (!svg.trim()) return svg

  const primary = hairPalette[colorName] ?? hairPalette.BrownDark
  const accent = mixWithBlack(primary, 0.35)

  return svg
    .replace(new RegExp(DEFAULT_FACIAL_HAIR_PRIMARY, 'gi'), primary)
    .replace(new RegExp(DEFAULT_FACIAL_HAIR_ACCENT, 'gi'), accent)
}

function applyHairColors(svg: string, colorName: string) {
  if (!svg.trim()) return svg

  const primary = hairPalette[colorName] ?? hairPalette.Brown
  const shadow = mixWithBlack(primary, 0.3)
  const mask = mixWithBlack(primary, 0.45)

  return svg
    .replace(new RegExp(DEFAULT_HAIR_PRIMARY, 'gi'), primary)
    .replace(new RegExp(DEFAULT_HAIR_MASK, 'gi'), mask)
    .replace(new RegExp(DEFAULT_HAIR_SHADOW, 'gi'), shadow)
}

function stripClothingBaseTransform(svg: string) {
  return svg.replace(
    /transform="translate\(\s*0(?:\.0+)?\s*,\s*170(?:\.0+)?\s*\)"/, 
    '',
  )
}

function applyClothingColors(svg: string, colorName: string) {
  if (!svg.trim()) return svg

  const primary = clothingPalette[colorName] ?? clothingPalette.Gray01
  const colorRectRegex = new RegExp(`(id="${COLOR_RECT_ID}"[^>]*fill=")(#[0-9a-fA-F]{6})`, 'g')

  let updated = svg.replace(colorRectRegex, (_, prefix) => `${prefix}${primary}`)

  if (updated === svg) {
    updated = updated.replace(/#FF5C5C/gi, primary)
  }

  return updated
}

function wrapWithTransform(svg: string, transform: string) {
  if (!svg.trim()) return svg
  return `<g transform="${transform}">${svg}</g>`
}

function buildCircleLayer(preferences: AvatarPreferences) {
  if (!preferences.isCircle) return ''
  const baseSvg = avatarComponentCatalog.background.circleBlue01
  const adjusted = applyCircleColor(
    extractInnerSvg(baseSvg),
    preferences.circleColor ?? DEFAULT_BACKGROUND_HEX,
  )
  return adjusted
}

function buildBodyLayer(preferences: AvatarPreferences) {
  const baseSvg = avatarComponentCatalog.body.lightDefault
  const skinHex = skinPalette[preferences.skinColor] ?? DEFAULT_BODY_SKIN_HEX
  const bodySvg = applySkinTones(extractInnerSvg(baseSvg), skinHex)
  return wrapWithTransform(
    bodySvg,
    `translate(${CANVAS_TRANSLATE_X} ${CANVAS_TRANSLATE_Y})`,
  )
}

function buildClothingLayer(preferences: AvatarPreferences) {
  const type = preferences.clotheType as AvatarClothingComponentName
  const baseSvg = avatarClothingComponents[type]
  if (!baseSvg) return ''

  const inner = extractInnerSvg(baseSvg)
  if (!inner.trim()) return ''

  const coloredBase = applyClothingColors(inner, preferences.clotheColor)
  let combined = stripClothingBaseTransform(coloredBase)

  if (type === 'GraphicShirt') {
    const graphicType = preferences.graphicType as AvatarClothingGraphicComponentName | undefined
    const graphicSvg = graphicType ? avatarClothingGraphicComponents[graphicType] : undefined
    if (graphicSvg) {
      const graphicInner = stripClothingBaseTransform(extractInnerSvg(graphicSvg))
      if (graphicInner.trim()) {
        combined = `${combined}\n${graphicInner}`
      }
    }
  }

  return wrapWithTransform(
    combined,
    `translate(${CLOTHING_TRANSLATE_X} ${CLOTHING_TRANSLATE_Y})`,
  )
}

function buildTopLayer(preferences: AvatarPreferences) {
  const topType = preferences.topType as AvatarTopComponentName
  const baseSvg = avatarTopComponents[topType] ?? avatarTopComponents.NoHair
  const inner = extractInnerSvg(baseSvg)

  if (!inner.trim()) {
    return ''
  }

  if (HEADWEAR_TOP_TYPES.has(topType)) {
    const topHex = topPalette[preferences.topColor] ?? topPalette.Black
    return applyHatColors(inner, topHex)
  }

  if (ACCESSORY_TOP_TYPES.has(topType)) {
    const topHex = topPalette[preferences.topColor] ?? topPalette.Black
    return applyAccessoryColors(inner, topHex, topType)
  }

  if (topType !== 'NoHair') {
    return applyHairColors(inner, preferences.hairColor)
  }

  return inner
}

function buildNoseLayer() {
  const baseSvg = avatarComponentCatalog.face.nose.default
  return extractInnerSvg(baseSvg)
}

function buildMouthLayer(preferences: AvatarPreferences) {
  const type = preferences.mouthType as AvatarMouthComponentName | undefined
  if (!type) return ''

  const baseSvg = avatarMouthComponents[type] ?? avatarMouthComponents.Default
  if (!baseSvg) return ''

  const inner = extractInnerSvg(baseSvg)
  return inner.trim()
    ? wrapWithTransform(inner, `translate(${FACE_TRANSLATE_X} ${FACE_TRANSLATE_Y})`)
    : ''
}

function buildEyesLayer(preferences: AvatarPreferences) {
  const type = preferences.eyeType as AvatarEyesComponentName | undefined
  if (!type) return ''

  const baseSvg = avatarEyesComponents[type] ?? avatarEyesComponents.Default
  if (!baseSvg) return ''

  const inner = extractInnerSvg(baseSvg)
  return inner.trim()
    ? wrapWithTransform(inner, `translate(${FACE_TRANSLATE_X} ${FACE_TRANSLATE_Y})`)
    : ''
}

function buildEyebrowsLayer(preferences: AvatarPreferences) {
  const type = preferences.eyebrowType as AvatarEyebrowComponentName | undefined
  if (!type) return ''

  const baseSvg = avatarEyebrowComponents[type] ?? avatarEyebrowComponents.Default
  if (!baseSvg) return ''

  const inner = extractInnerSvg(baseSvg)
  return inner.trim()
    ? wrapWithTransform(inner, `translate(${FACE_TRANSLATE_X} ${FACE_TRANSLATE_Y})`)
    : ''
}

function buildFacialHairLayer(preferences: AvatarPreferences) {
  const type = preferences.facialHairType as AvatarFacialHairComponentName | undefined
  if (!type || type === 'Blank') return ''

  if (!Object.prototype.hasOwnProperty.call(avatarFacialHairComponents, type)) {
    return ''
  }

  const baseSvg = avatarFacialHairComponents[type]
  const inner = extractInnerSvg(baseSvg)
  if (!inner.trim()) return ''

  return applyFacialHairColors(inner, preferences.facialHairColor)
}

function buildAccessoryLayer(preferences: AvatarPreferences) {
  const type = preferences.accessoriesType as AvatarAccessoryComponentName | undefined
  if (!type || type === 'Blank') return ''

  if (!Object.prototype.hasOwnProperty.call(avatarAccessoryComponents, type)) {
    return ''
  }

  const baseSvg = avatarAccessoryComponents[type]
  const inner = extractInnerSvg(baseSvg)
  if (!inner.trim()) return ''

  return applyAccessoryColors(inner, undefined, type)
}

export function generateAvatarLayers(preferences: AvatarPreferences) {
  return [
    buildCircleLayer(preferences),
    buildBodyLayer(preferences),
    buildClothingLayer(preferences),
    buildTopLayer(preferences),
    buildNoseLayer(),
    buildMouthLayer(preferences),
    buildEyesLayer(preferences),
    buildEyebrowsLayer(preferences),
    buildFacialHairLayer(preferences),
    buildAccessoryLayer(preferences),
  ].filter(Boolean)
}

export interface AvatarSvgOptions {
  includeXmlDeclaration?: boolean
}

const isBrowser = typeof window !== 'undefined'
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const globalBuffer: any =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  typeof globalThis !== 'undefined' && (globalThis as any).Buffer
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ? (globalThis as any).Buffer
    : null

function encodeSvgToBase64(svg: string) {
  if (isBrowser && typeof window.btoa === 'function') {
    return window.btoa(unescape(encodeURIComponent(svg)))
  }

  if (globalBuffer?.from) {
    return globalBuffer.from(svg, 'utf-8').toString('base64')
  }

  throw new Error('No available Base64 encoder for current environment.')
}

export function generateAvatarSvg(
  preferences: AvatarPreferences,
  options: AvatarSvgOptions = {},
) {
  const { includeXmlDeclaration = true } = options
  const layers = generateAvatarLayers(preferences).join('\n')
  const svgBody = `<svg viewBox="${AVATAR_VIEWBOX}" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">${layers}</svg>`

  if (!includeXmlDeclaration) {
    return svgBody
  }

  return `<?xml version="1.0" encoding="UTF-8"?>\n${svgBody}`
}

export interface AvatarDataUriOptions extends AvatarSvgOptions {
  encoding?: 'uri' | 'base64'
}

export function generateAvatarSvgDataUri(
  preferences: AvatarPreferences,
  options: AvatarDataUriOptions = {},
) {
  const { encoding = 'base64', ...svgOptions } = options
  const svg = generateAvatarSvg(preferences, svgOptions)

  if (encoding === 'base64') {
    const base64 = encodeSvgToBase64(svg)
    return `data:image/svg+xml;base64,${base64}`
  }

  const encoded = encodeURIComponent(svg)
  return `data:image/svg+xml;utf8,${encoded}`
}
